"use client";
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import Head from 'next/head';
import { useSearchParams } from 'next/navigation'
import { use100vh } from 'react-div-100vh';
import { APP_ACTION_TYPES } from '@/store';
import { AppContext } from '@/context';
import { getConfigPage } from '@/apiRequest/page';
import { lightApp } from '@/constants/platform';
import SENSORS from '@/constants/sensor';
import { setBaseSize } from '@/utils';
import Sensor from '@/utils/sensor';
import { getUrlQuery } from '@/utils/link';
import { jump, setPageTitle } from '@/jsBridge/sdk';
import { aLink } from '@/jsBridge/sdk/openApp';
import ClipBoard from '@/components/ClipBoard';
import BrowserViewGuide from '@/components/BrowserViewGuide';
import TemplatePage from '../components/TemplatePage';
import Exception from '../components/Exception';
import Styles from './index.module.scss';

const EmptyExceptionImage = require('@/public/assets/images/exception/empty-exception-center.png')?.default?.src;

let POS_X, POS_Y;
let IS_TOUCHING = false;
const PAGE_SLUGS = [];
const SWIPE_UP_OFFSET_THRESHOLD = 75; // 向上滑动
const SWIPE_DOWN_OFFSET_THRESHOLD = -75; // 向下滑动
const SWIPE_OFFSET_THRESHOLD_ABS = 50; // 左右滑动

const ActivityCenterPage = ({ params = {} }) => {
  const [, updateState] = useState();
  const [guidelineShow, setGuidelineShow] = useState(false);
  const [pageInfo, setPageInfo] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [, setStartTouch] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [isIdle, setIsIdle] = useState(false);

  const clipboardRef = useRef(null);
  const { store, dispatch } = useContext(AppContext);
  const { slug } = params;
  const searchParams = useSearchParams();
  const pageSlug= searchParams.get('pageSlug');

  const IS_LIGHT = store && store.platform === lightApp;
  const IS_ANDROID = store && store.device === 'Android';
  const height100vh = use100vh();
  const height100vhValue = (IS_LIGHT && IS_ANDROID)
    ? document.documentElement.clientHeight || document.body.clientHeight
    : height100vh;

  const fetchPageInfo = async () => {
    try {
      const workData = await getConfigPage({ slug }) || {};
      const tempConfig = JSON.parse(workData.config_json);
      const { pages: tempPages = [] } = tempConfig;
      const config = {
        ...tempConfig,
        pages: tempPages.filter(({ common = {} }) => common.status === 'online'),
      };
      setPageInfo({ ...workData, config })
      setIsFetched(true);

      let tempCurPage = null;
      (config.pages || []).forEach((elem) => {
        const { common = {}, header = {} } = elem;
        if (pageSlug && common.slug === pageSlug) {
          tempCurPage = { ...common, title: header.title?.label };
        }
        PAGE_SLUGS.push(common.slug);
      });

      const firstPage = config.pages[0] || {};
      const firstPageData = {
        ...firstPage.common,
        title: firstPage.header?.title.label,
      };

      setCurrentPage(tempCurPage || firstPageData);
    } catch (e) {
      setIsFetched(true);
    }
  };

  const forceUpdate = useCallback(() => updateState({}), []);

  const updateShareInfo = (shareData = {}) => {
    dispatch({
      type: APP_ACTION_TYPES.UPDATE_SHARE_INFO,
      payload: {
        shareData: {
          ...store.shareData,
          ...shareData,
        },
      },
    });
  };

  const findTargetPage = (targetSlug, arr) => {
    const res = !targetSlug ? arr : arr.filter(({ common } = {} ) => common.slug === targetSlug);
    return res.length > 0 ? res : arr;
  };

  const { general = {}, pages = [] } = pageInfo.config || {};

  const handleSensorTrack = (eventName, params = {}) => {
    Sensor.sensorMd(eventName, {
      page_title_h5: general.sensorPageTitle,
      ...params,
    });
  };

  const handleTouchStart = (e) => {
    e.stopPropagation();
    const { targetTouches = [] } = e;
    const { clientX, clientY } = targetTouches[0] || {};
    POS_X = clientX;
    POS_Y = clientY;
    IS_TOUCHING = false;
    setStartTouch(true);
  };

  const handleTouchMove = (e) => {
    if (IS_TOUCHING) {
      return;
    }

    const { changedTouches = [] } = e;
    const { target, clientX, clientY } = changedTouches[0] || {};
    const { className: cls, tagName } = target;

    if (typeof cls === 'string' && Math.abs(clientX - POS_X) > SWIPE_OFFSET_THRESHOLD_ABS && (cls.includes('xgplayer') || cls.includes('swiper'))) {
      return;
    }

    if (typeof tagName === 'string' && Math.abs(clientX - POS_X) > SWIPE_OFFSET_THRESHOLD_ABS && (tagName.toUpperCase() === 'VIDEO' || tagName.toUpperCase() === 'SVG' || tagName.toUpperCase() === 'DIV')) {
      return;
    }

    const offsetY = clientY - POS_Y;
    const { slug: curSlug } = currentPage;
    const curIndex = PAGE_SLUGS.indexOf(curSlug);

    // 手势向上，页面向下滑动
    if (offsetY < SWIPE_DOWN_OFFSET_THRESHOLD && curIndex < PAGE_SLUGS.length - 1) {
      e.preventDefault();
      e.stopPropagation();
      IS_TOUCHING = true;
      const currentElem = document.getElementById(curSlug);
      if (currentElem) {
        currentElem.style.transform = `translateY(-${(curIndex + 1) * height100vhValue}px)`;

        const curPageInfo = pageInfo.config?.pages[curIndex + 1];
        const { common = {}, header = {} } = curPageInfo;
        const curPage = {
          ...common,
          title: header?.title.label,
        };

        setCurrentPage(curPage);
        document.querySelectorAll('video')?.forEach((elem) => elem?.pause());
      }
    }

    // 手势向下，页面向上滑动
    else if (offsetY > SWIPE_UP_OFFSET_THRESHOLD && curIndex > 0) {
      e.preventDefault();
      e.stopPropagation();
      IS_TOUCHING = true;
      const prevElem = document.getElementById(PAGE_SLUGS[curIndex - 1]);
      if (prevElem) {
        prevElem.style.transform = `translateY(-${(curIndex - 1) * height100vhValue}px)`;

        const curPageInfo = pageInfo.config?.pages[curIndex - 1];
        const { common = {}, header = {} } = curPageInfo;
        const curPage = {
          ...common,
          title: header?.title.label,
        };

        setCurrentPage(curPage);
        document.querySelectorAll('video')?.forEach((elem) => elem?.pause());
      }
    }
  };

  const handleTouchEnd = () => {
    IS_TOUCHING = false;
    setStartTouch(false);
  };

  const handleSlideNext = () => {
    const { slug: curSlug } = currentPage;
    const curIndex = PAGE_SLUGS.indexOf(curSlug);
    const currentElem = document.getElementById(curSlug);
    if (currentElem) {
      currentElem.style.transform = `translateY(-${(curIndex + 1) * height100vhValue}px)`;

      const curPageInfo = pageInfo.config?.pages[curIndex + 1];
      const { common = {}, header = {} } = curPageInfo;
      const curPage = {
        ...common,
        title: header?.title.label,
      };

      setCurrentPage(curPage);
    }
  };

  useEffect(() => {
    if (!pageSlug) {
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: false });
      return () => {
        document.removeEventListener('touchstart', handleTouchStart, { passive: false });
        document.removeEventListener('touchmove', handleTouchMove, { passive: false });
        document.removeEventListener('touchend', handleTouchEnd, { passive: false });
      };
    }
  }, [currentPage, pageSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (store.isSensorInit && currentPage.title) {
      handleSensorTrack(SENSORS.LIGHT_H5_PV, { model_tab: currentPage.title });
    }
  }, [currentPage.title, store.isSensorInit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (slug) {
      fetchPageInfo();
      setBaseSize();
      forceUpdate();
    }
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if(IS_LIGHT && pageInfo.name) {
      setPageTitle({ title: pageInfo.name });
    }
  }, [pageInfo.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentPage.slug) {
      const curShareInfo = currentPage?.share || {};
      const pageSlugQuery = getUrlQuery('pageSlug') ? `?pageSlug=${getUrlQuery('pageSlug')}` : '';
      updateShareInfo({
        title: curShareInfo.title,
        desc: curShareInfo.description,
        image: curShareInfo.wxIcon,
        link: `${window.location.href.split('?')[0]}${pageSlugQuery}`,
      });
    }
  }, [currentPage.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseDownloadGuideline = () => {
    setGuidelineShow(false);
  };

  const handleJump = (param = {}) => {
    const result = jump(param);
    if (result.length > 0) {
      if (!IS_LIGHT) {
        clipboardRef.current.initClipBoard(result[1]);
      }
      if (result[2]) {
        setTimeout(() => {
          setGuidelineShow(true);
        }, 300);
      }
      aLink(result[0]);
    }
  };

  const renderPages = () => {
    const targetPages = findTargetPage(pageSlug, pages);
    return (
      <div className={Styles.pages}>
        {
          targetPages.map(({ header, common, templates = {} }, index) => {
            const { slug: uid } = common || {};
            const isPageVisible = uid === currentPage.slug;

            return (
              <TemplatePage
                key={uid}
                data={{
                  general, header, common, templates, currentPageIndex: PAGE_SLUGS.indexOf(currentPage.slug), currentPageSlug: currentPage.slug,
                  pageHeight: height100vhValue, pageIndex: index, pagesLength: targetPages.length,
                }}
                isLight={IS_LIGHT}
                isPageVisible={isPageVisible}
                isIdle={isIdle}
                onSlideNext={handleSlideNext}
                onChangeIdle={setIsIdle}
                onRedirect={handleJump}
                onSensorTrack={handleSensorTrack}
              />
            );
          })
        }
      </div>
    );
  };

  const renderEmpty = () => <Exception className={Styles.exception} image={EmptyExceptionImage} tips="没有找到对应内容" />;

  return (
    <React.Fragment>
      <Head>
        <title>{general.pageTitle}</title>
      </Head>
      <div className={Styles.root} style={{ height: height100vhValue }}>
        <style global jsx>
          {`
            html, body {
              overflow: hidden;
              font-family: "SourceHanSans","PingFang SC","Roboto",sans-serif;
            }
          `}
        </style>
        {
          guidelineShow && <BrowserViewGuide callback={handleCloseDownloadGuideline} />
        }
        <ClipBoard ref={clipboardRef} />
        {
          (isFetched && pages.length < 1) ? renderEmpty() : renderPages()
        }
      </div>
    </React.Fragment>
  );
};

export default ActivityCenterPage;
